import { useEffect, useState } from "react";

import { get, identity } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";
import { BasicModal } from "@similarweb/ui-components";

import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import useSetTimeout from "app/pages/sales-intelligence/hooks/useSetTimeout";
import type { EnrichmentModalStage } from "app/pages/sales-intelligence/modules/enrichment/components/type";
import { CSV_LIMIT_VALUE, ENRICHING_ERROR } from "app/pages/sales-intelligence/modules/enrichment/constants/constants";
import { EUploadCSVEnrichmentStage } from "app/pages/sales-intelligence/modules/enrichment/constants/enums";
import useEnrichmentList from "app/pages/sales-intelligence/modules/enrichment/queries/useEnrichmentList";
import { clearEnrichmentParsing } from "app/pages/sales-intelligence/modules/enrichment/store/actions/enrichment";
import {
    getEnrichedCsvThunk,
    parseEnrichmentCsvThunk,
    postEnrichmentCsvThunk,
} from "app/pages/sales-intelligence/modules/enrichment/store/effects/enrichment";
import { selectPreview } from "app/pages/sales-intelligence/modules/enrichment/store/selectors/selectors";
import type { EnrichmentErrorState, PostCsvProps } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import { EnrichmentStatus } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";
import type { ParseDomainsResult } from "app/pages/sales-intelligence/sub-modules/management/components/types";
import { WithKeysInput } from "app/pages/sales-intelligence/sub-modules/management/hocs/withKeysInput";
import { selectAllUniqueWebsites } from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import { downloadFile } from "app/pages/sales-intelligence/utils/downloadFile";
import type { IProModalCustomStyles } from "pro-features/components/Modals/src/ProModal";

import { ModalContent } from "../ModalContent/ModalContent";

import type { UploadEnrichmentListModalProps } from "./type";

const CUSTOM_MODAL_STYLES: IProModalCustomStyles = {
    content: { padding: 0, width: "auto", transition: "0.5s ease-out" },
};

const CLOSE_ICON_CONFIGS = { show: false };
const REFRESH_TIMEOUT = 5000;
const NOT_VALID_CSV_ERROR = "Csv is not valid";

export const UploadEnrichmentListModal = ({
    isOpen,
    onOpenStateChange,
    file,
    setFile,
    onFileChange,
}: UploadEnrichmentListModalProps) => {
    const dispatch = useDispatch();
    const [modalStage, setModalStage] = useState<EnrichmentModalStage>(EUploadCSVEnrichmentStage.Upload);
    const [errorText, setErrorText] = useState("");

    const { translate } = useTranslate();

    const [selectedHeader, setSelectedHeader] = useState("");

    const [csvId, setCsvId] = useState<string | null>(null);
    const [enrichmentProps, setEnrichmentProps] = useState<PostCsvProps>();
    const preview = useSelector(selectPreview);
    const uniqDomainsTracked = useSelector(selectAllUniqueWebsites).length;
    const uniqDomainsQuota = salesSettingsHelper.getQuotaLimit();

    const { refetch } = useEnrichmentList({ enabled: false });

    const localSetTimeout = useSetTimeout();

    const onConfirmAsync = async () => {
        if (file && enrichmentProps) {
            enrichmentTrackingService.csv.company.trackApproveEnrichment(
                preview?.matchedRows,
                enrichmentProps?.columns.length,
            );

            const { columns, country, domainFieldName } = enrichmentProps;
            const enrichState = await dispatch(
                postEnrichmentCsvThunk({
                    columns,
                    domainFieldName,
                    country,
                    file,
                }),
            );

            if (enrichState.id === null) {
                return Promise.reject((enrichState as EnrichmentErrorState).error);
            }

            setCsvId(enrichState.id);
        }
    };

    const refreshCsvStatus = async () => {
        const { data } = await refetch();

        const currentCsvState = data?.find((csvStatus) => csvStatus.id === csvId);

        if (currentCsvState?.status === EnrichmentStatus.InProgress) {
            localSetTimeout(() => {
                refreshCsvStatus();
            }, REFRESH_TIMEOUT);
        }
        if (currentCsvState?.status === EnrichmentStatus.Succeeded) {
            const result = await dispatch(getEnrichedCsvThunk(JSON.stringify(csvId)));

            enrichmentTrackingService.csv.company.trackSuccessEnrichment(
                preview.matchedRows,
                enrichmentProps?.columns.length ?? 0,
            );

            if (currentCsvState.domainsCount + uniqDomainsTracked <= uniqDomainsQuota) {
                setModalStage(EUploadCSVEnrichmentStage.Downloading);
            } else {
                onOpenStateChange(false);
            }

            file && downloadFile(file.name, result);

            void quotaQueryClient.invalidateDataCreditsState();
        }
        if (currentCsvState?.status === EnrichmentStatus.Failed) {
            onOpenStateChange(false);
            setModalStage(EUploadCSVEnrichmentStage.Upload);

            enrichmentTrackingService.csv.company.trackFailureEnrichment(
                preview.matchedRows,
                enrichmentProps?.columns.length ?? 0,
            );
            Sentry.error("error in refreshCsvStatus in UploadEnrichmentListModal.tsx", new Error(ENRICHING_ERROR));
        }
    };

    useDidUpdate(() => {
        if (csvId) {
            localSetTimeout(() => {
                refreshCsvStatus();
            }, REFRESH_TIMEOUT);
        }
    }, [csvId]);

    const parseFile = async () => {
        if (file) {
            const result = await dispatch(parseEnrichmentCsvThunk(file, CSV_LIMIT_VALUE));
            return result as ParseDomainsResult;
        }
    };

    useEffect(() => {
        if (file) {
            parseFile().then((result) => {
                if (result && result.totalRowCount) {
                    enrichmentTrackingService.csv.company.trackFileParse(result.totalRowCount);

                    const defaultColumn = get(result, "domainColumn", "");
                    if (defaultColumn) {
                        setSelectedHeader(defaultColumn);
                        setModalStage(EUploadCSVEnrichmentStage.Enrichment);
                    }
                }
            });
        }
    }, [file]);

    useEffect(() => {
        enrichmentProps &&
            onConfirmAsync()
                .then(() => {
                    setModalStage(EUploadCSVEnrichmentStage.Preparing);
                })
                .catch((error) => {
                    setModalStage(EUploadCSVEnrichmentStage.Error);
                    setErrorText(
                        translate(
                            error === NOT_VALID_CSV_ERROR
                                ? "si.enrichment.csv.is.not.valid"
                                : "si.common.error.some-error-occurred",
                        ),
                    );
                    Sentry.error("Error uploading CSV for lead enrichment", error);
                });
    }, [enrichmentProps]);

    const onClose = () => {
        onOpenStateChange(false);
        setFile(null);
    };

    useEffect(() => {
        if (isOpen) {
            dispatch(clearEnrichmentParsing());
            setModalStage(EUploadCSVEnrichmentStage.Upload);
        }
    }, [isOpen]);

    const onReplace = () => {
        setModalStage(EUploadCSVEnrichmentStage.Upload);
        setFile(null);
    };

    const onConfirm = (props: PostCsvProps) => {
        setEnrichmentProps(props);
    };

    const fileName = file?.name || "";

    return (
        <BasicModal
            data-automation="enrichment-upload-csv-modal"
            customStyles={CUSTOM_MODAL_STYLES}
            isOpen={isOpen}
            closeIconConfig={CLOSE_ICON_CONFIGS}
            shouldCloseOnOverlayClick
        >
            <WithKeysInput
                onEnter={identity}
                onEscape={() => onClose()}
            >
                <ModalContent
                    setSelectedHeader={setSelectedHeader}
                    onClose={onClose}
                    onConfirm={onConfirm}
                    onFileChange={onFileChange}
                    onReplace={onReplace}
                    selectedHeader={selectedHeader}
                    fileName={fileName}
                    modalStage={modalStage}
                    errorText={errorText}
                />
            </WithKeysInput>
        </BasicModal>
    );
};
